import Link from "next/link";

import CodeBlock from "@/components/docs/CodeBlock";
import MethodBadge from "@/components/docs/MethodBadge";
import { DocCode, DocList, DocP, DocSection, DocTable } from "@/components/docs/Prose";
import {
  API,
  DEVELOPERS_URL,
  OPENAPI_PATH,
  endpointAnchor,
  getEndpoint,
  rateTier,
} from "@/lib/docs/api";
import { docPath } from "@/lib/docs/content";

const LINK = "font-medium text-[#335CFF] underline decoration-[#335CFF]/30 underline-offset-[3px] hover:decoration-[#335CFF]";

const ERROR_ENVELOPE = JSON.stringify(
  { success: false, error: { code: "VALIDATION_ERROR", message: "title is required" } },
  null,
  2,
);

const GettingStartedGuide = () => {
  const account = getEndpoint("get-account");
  const createWebhook = getEndpoint("create-webhook");
  const tierEndpoints = (tier: string) =>
    API.endpoints.filter((endpoint) => endpoint.rateTier === tier);

  return (
    <div className="flex flex-col gap-12">
      <DocSection id="api-key" title="1. Create an API key">
        <DocP>
          Keys are created on the{" "}
          <a href={DEVELOPERS_URL} className={LINK} target="_blank" rel="noreferrer">
            Developers page
          </a>{" "}
          of the Crayo app. Every key starts with <DocCode>{API.apiKeyPrefix}</DocCode> and acts as
          the whole account, so keep it on a server and read it from an environment variable rather
          than shipping it in a client app.
        </DocP>
        <DocList
          items={[
            `An account can hold up to ${API.maxApiKeys} active keys at once, each with its own name, so every app, environment or teammate can have one that is revoked on its own.`,
            `Expiry is picked at creation: ${API.apiKeyExpiries.map((expiry) => expiry.label.toLowerCase()).join(", ")}. The dashboard defaults to ${API.apiKeyExpiries.find((expiry) => expiry.id === API.defaultApiKeyExpiry)?.label.toLowerCase()}. An expired key answers 401 exactly like a revoked one.`,
            "Only a hash of the key is stored, so a lost key cannot be recovered. Create a new one, deploy it, then revoke the old one.",
          ]}
        />
      </DocSection>

      <DocSection id="authentication" title="2. Send it as a bearer token">
        <DocP>
          The base URL is <DocCode>{API.baseUrl}</DocCode>. Every request carries the key in the
          Authorization header. There is nothing else to sign and no session to keep alive. POST and
          PATCH bodies are JSON with a <DocCode>Content-Type: application/json</DocCode> header,
          GET and DELETE take no body, and request bodies are capped at{" "}
          {API.limits.labels.maxRequestBytes}.
        </DocP>
        {account && (
          <>
            <CodeBlock code={account.curl} label="curl" />
            <DocP>
              A good first call is{" "}
              <Link href={endpointAnchor(account)} prefetch={false} className={LINK}>
                GET /v1/account
              </Link>
              . It is free and returns your plan, credit balances, storage usage and the rate limits
              that apply to the key.
            </DocP>
          </>
        )}
      </DocSection>

      <DocSection id="credits" title="3. Understand credits">
        <DocP>
          The API spends the same credits as the Crayo app, from the same balance. Reads are free.
          Anything that generates or renders media costs credits, and they are spent when a request
          is accepted rather than when it finishes well, so a failed render or an AutoClip job that
          finds fewer clips than requested leaves them spent. View Tracker is the exception: a
          lookup that fails on our side is refunded.
        </DocP>
        <DocTable
          headers={["Credit type", "Spent on"]}
          rows={API.creditTypes.map((type) => [type.label, type.spentOn])}
        />
        <DocP>
          Storage is a separate budget. Uploads and imports cost no credits but count against your
          plan storage, and so does everything you generate or render. Going over returns{" "}
          <DocCode>402 STORAGE_LIMIT_EXCEEDED</DocCode>.
        </DocP>
      </DocSection>

      <DocSection id="rate-limits" title="4. Rate limits">
        <DocP>
          Limits are per API key. Most endpoints share the default budget, while the expensive and
          the pollable endpoints get their own tiers.
        </DocP>
        <DocTable
          headers={["Tier", "Per second", "Per minute", "Applies to"]}
          rows={API.generalRateTiers.map((tier) => [
            rateTier(tier).label,
            String(rateTier(tier).perSecond),
            String(rateTier(tier).perMinute),
            tier === "default" ? (
              "Every endpoint not listed in another tier"
            ) : (
              <span className="flex flex-col gap-1">
                {tierEndpoints(tier).map((endpoint) => (
                  <Link
                    key={endpoint.id}
                    href={endpointAnchor(endpoint)}
                    prefetch={false}
                    className="flex items-center gap-2 font-mono text-[11.5px] text-[#525866] hover:text-[#335CFF]"
                  >
                    <MethodBadge method={endpoint.method} />
                    {endpoint.path}
                  </Link>
                ))}
              </span>
            ),
          ])}
        />
        <DocP>
          Every response carries <DocCode>RateLimit-Limit</DocCode>,{" "}
          <DocCode>RateLimit-Remaining</DocCode> and <DocCode>RateLimit-Reset</DocCode>. Going over
          returns 429 with code <DocCode>RATE_LIMITED</DocCode> and a{" "}
          <DocCode>Retry-After</DocCode> header in seconds.
        </DocP>
        <DocP>
          View Tracker has its own, higher limits that count per account rather than per key, so a
          busy tracker integration never eats the budget of your other calls.
        </DocP>
        <DocTable
          headers={["Tier", "Per second", "Per minute", "Applies to"]}
          rows={API.viewTracker.rateTiers.map(({ tier, appliesTo }) => [
            rateTier(tier).label,
            String(rateTier(tier).perSecond),
            String(rateTier(tier).perMinute),
            <span key="a" className="font-mono text-[11.5px]">
              {appliesTo}
            </span>,
          ])}
        />
      </DocSection>

      <DocSection id="errors" title="5. Errors">
        <DocP>
          Failures come back in one shape, with a stable <DocCode>code</DocCode> you can branch on
          and a <DocCode>message</DocCode> written for a human reading a log. Branch on the code:
          messages get clearer over time, codes do not change.
        </DocP>
        <CodeBlock code={ERROR_ENVELOPE} label="error response" />
        <DocP>These four can come back from any endpoint.</DocP>
        <DocTable
          headers={["Status", "Code", "When"]}
          rows={API.sharedErrors.map((error) => [
            String(error.status),
            <span key="c" className="font-mono text-[12px] text-[#101828]">
              {error.code}
            </span>,
            error.when,
          ])}
        />
        <DocP>
          Endpoint-specific codes are listed on each endpoint. The most common across the API are
          below, with the number of endpoints that can return each one.
        </DocP>
        <DocTable
          headers={["Status", "Code", "Endpoints"]}
          rows={API.errorIndex
            .filter((error) => error.endpoints.length >= 3)
            .map((error) => [
              String(error.status),
              <span key="c" className="font-mono text-[12px] text-[#101828]">
                {error.code}
              </span>,
              String(error.endpoints.length),
            ])}
        />
      </DocSection>

      <DocSection id="webhooks" title="6. Webhooks">
        <DocP>
          Renders, AutoClip jobs, speech enhancements and tracker refreshes run in the background.
          Register an https endpoint with{" "}
          {createWebhook && (
            <Link href={endpointAnchor(createWebhook)} prefetch={false} className={LINK}>
              POST /v1/webhooks
            </Link>
          )}{" "}
          and we send one signed POST the moment the work is done. The signing secret starts with{" "}
          <DocCode>{API.webhookSecretPrefix}</DocCode> and is returned exactly once.
        </DocP>
        <DocTable
          headers={["Event", "Fires when"]}
          rows={API.webhookEvents.map((event) => [
            <span key="e" className="font-mono text-[12px]">
              {event.id}
            </span>,
            event.description,
          ])}
        />
        <DocP>
          Each delivery carries a <DocCode>crayo-signature</DocCode> header shaped like{" "}
          <DocCode>t=1755785271,v1=&lt;hex&gt;</DocCode>: an HMAC-SHA256 over{" "}
          <DocCode>{"<t>.<raw body>"}</DocCode> using your webhook secret. Deliveries are attempted
          up to {API.limits.webhookAttempts} times, backing off exponentially from{" "}
          {API.limits.webhookBackoffSeconds} seconds, and an endpoint that fails{" "}
          {API.limits.webhookDisableAfterFailures} times in a row is disabled. Read the{" "}
          <Link href={docPath("webhooks")} prefetch={false} className={LINK}>
            Webhooks reference
          </Link>{" "}
          for the endpoints and payloads.
        </DocP>
      </DocSection>

      <DocSection id="next" title="Next steps">
        <DocList
          items={[
            <span key="autoclip">
              Clip a long video:{" "}
              <Link href={docPath("autoclip")} prefetch={false} className={LINK}>
                AutoClip
              </Link>
              .
            </span>,
            <span key="projects">
              Compose and render your own video:{" "}
              <Link href={docPath("projects")} prefetch={false} className={LINK}>
                Projects
              </Link>{" "}
              and{" "}
              <Link href={docPath("exports")} prefetch={false} className={LINK}>
                Exports
              </Link>
              .
            </span>,
            <span key="openapi">
              Generate a client from the{" "}
              <Link href={docPath("openapi")} prefetch={false} className={LINK}>
                OpenAPI specification
              </Link>{" "}
              at <DocCode>{OPENAPI_PATH}</DocCode>.
            </span>,
            <span key="support">
              Missing an endpoint? Ask in{" "}
              <a href={API.discordUrl} className={LINK} target="_blank" rel="noreferrer">
                Discord
              </a>{" "}
              or email {API.supportEmail}.
            </span>,
          ]}
        />
      </DocSection>
    </div>
  );
};

export default GettingStartedGuide;
